import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";
import {
  FaUsers,
  FaChartLine,
  FaDollarSign,
  FaCopy,
  FaUserPlus,
  FaCheck,
  FaSitemap,
  FaInfoCircle,
  FaArrowDown,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const Referrals = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [statsResponse, treeResponse] = await Promise.all([
        api.get("/referrals/stats"),
        api.get("/referrals/tree?depth=3"),
      ]);
      setStats(statsResponse.data.stats);
      setTree(treeResponse.data.tree);
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const renderTreeNode = (node, level = 1) => {
    const levelStyles = {
      1: {
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
        text: "text-emerald-300",
      },
      2: {
        bg: "bg-cyan-500/10",
        border: "border-cyan-500/20",
        text: "text-cyan-300",
      },
      3: {
        bg: "bg-violet-500/10",
        border: "border-violet-500/20",
        text: "text-violet-300",
      },
    };
    const style = levelStyles[level] || levelStyles[3];

    return (
      <div key={node._id} className="ml-4 mt-2">
        <div
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${style.bg} border ${style.border}`}
        >
          <div
            className={`w-8 h-8 rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0`}
          >
            <FaUsers className={`text-xs ${style.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white truncate">
                {node.fullName}
              </p>
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border capitalize ${
                  node.isActivated ?
                    "text-emerald-300 bg-emerald-500/15 border-emerald-500/30"
                  : "text-amber-300 bg-amber-500/15 border-amber-500/30"
                }`}
              >
                {node.isActivated ? "Active" : "Pending"}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[11px] text-slate-500">
                @{node.username}
              </span>
              <span className="w-0.5 h-0.5 bg-slate-600 rounded-full" />
              <span className={`text-[11px] font-medium ${style.text}`}>
                L{level}
              </span>
              <span className="w-0.5 h-0.5 bg-slate-600 rounded-full" />
              <span className="text-[11px] text-slate-400">
                ${node.totalEarnings?.toFixed(2) || "0.00"}
              </span>
            </div>
          </div>
        </div>
        {node.children && node.children.length > 0 && (
          <div className="border-l border-white/[0.06] ml-4">
            {node.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-[60vh] gap-3">
        <div className="w-12 h-12 rounded-full border-[3px] border-cyan-400/30 border-t-cyan-400 animate-spin" />
        <p className="text-sm text-slate-400 animate-pulse">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-6">
      {/* Hero Card */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-600 p-5 shadow-xl shadow-emerald-500/10">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <FaUsers className="text-lg text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Referral Network</h1>
              <p className="text-[11px] text-emerald-100/80 font-medium">
                3-Level Commission System
              </p>
            </div>
          </div>

          <div className="bg-white/15 backdrop-blur-xl rounded-xl px-4 py-3 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-emerald-100 font-medium uppercase tracking-wider">
                  Total Earnings
                </p>
                <p className="text-2xl font-extrabold text-white tracking-tight mt-0.5">
                  ${stats?.totalReferralEarnings?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                <FaDollarSign className="text-lg text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Level Stats - scrollable chips */}
      <div className="relative">
        <div className="absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none rounded-r-2xl" />
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {[
            {
              label: "Total",
              value: stats?.totalReferrals || 0,
              icon: FaUsers,
              gradient: "from-emerald-500 to-teal-600",
              glow: "shadow-emerald-500/20",
            },
            {
              label: "Level 1 (10%)",
              value: stats?.level1.count || 0,
              sub: `$${stats?.level1.earnings?.toFixed(2) || "0.00"}`,
              icon: FaUserPlus,
              gradient: "from-sky-500 to-cyan-600",
              glow: "shadow-sky-500/20",
            },
            {
              label: "Level 2 (3%)",
              value: stats?.level2.count || 0,
              sub: `$${stats?.level2.earnings?.toFixed(2) || "0.00"}`,
              icon: FaArrowDown,
              gradient: "from-violet-500 to-purple-600",
              glow: "shadow-violet-500/20",
            },
            {
              label: "Level 3 (2%)",
              value: stats?.level3.count || 0,
              sub: `$${stats?.level3.earnings?.toFixed(2) || "0.00"}`,
              icon: FaChartLine,
              gradient: "from-amber-500 to-orange-500",
              glow: "shadow-amber-500/20",
            },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className={`snap-start flex-shrink-0 w-[130px] rounded-xl bg-gradient-to-br ${stat.gradient} p-3.5 shadow-lg ${stat.glow} border border-white/10`}
              >
                <Icon className="text-white/70 text-xs mb-2" />
                <p className="text-xl font-extrabold text-white leading-none">
                  {stat.value}
                </p>
                <p className="text-[10px] text-white/70 font-medium mt-1">
                  {stat.label}
                </p>
                {stat.sub && (
                  <p className="text-[10px] text-white/50 font-medium">
                    {stat.sub}
                  </p>
                )}
              </div>
            );
          })}
          <div className="flex-shrink-0 w-4" />
        </div>
      </div>

      {/* Referral Link Card */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaCopy className="text-cyan-400 text-xs" />
          Your Referral Link
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex-1 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 overflow-hidden">
            <p className="text-xs text-slate-400 font-mono truncate">
              {window.location.origin}/register?ref={user?.referralCode}
            </p>
          </div>
          <button
            onClick={copyReferralLink}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-semibold active:scale-[0.97] transition-transform touch-manipulation"
          >
            <FaCopy className="text-[10px]" />
            Copy
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Code:{" "}
          <span className="text-cyan-300 font-semibold">
            {user?.referralCode}
          </span>
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 p-1 rounded-xl bg-white/[0.04] border border-white/[0.06]">
        {[
          { id: "stats", label: "Breakdown", icon: FaChartLine },
          { id: "tree", label: "Tree", icon: FaSitemap },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 touch-manipulation ${
                activeTab === tab.id ?
                  "bg-white/10 text-white shadow-sm"
                : "text-slate-500"
              }`}
            >
              <Icon className="text-[10px]" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "stats" && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 space-y-2.5">
          {[
            {
              level: "Level 1",
              desc: "Direct Referrals",
              pct: "10%",
              count: stats?.level1.count || 0,
              earned: stats?.level1.earnings?.toFixed(2) || "0.00",
              color: "text-emerald-400",
              bg: "bg-emerald-500/10",
            },
            {
              level: "Level 2",
              desc: "Indirect Referrals",
              pct: "3%",
              count: stats?.level2.count || 0,
              earned: stats?.level2.earnings?.toFixed(2) || "0.00",
              color: "text-cyan-400",
              bg: "bg-cyan-500/10",
            },
            {
              level: "Level 3",
              desc: "Third Generation",
              pct: "2%",
              count: stats?.level3.count || 0,
              earned: stats?.level3.earnings?.toFixed(2) || "0.00",
              color: "text-violet-400",
              bg: "bg-violet-500/10",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-white/[0.03] border border-white/[0.05]"
            >
              <div
                className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}
              >
                <span className={`text-xs font-bold ${item.color}`}>
                  {item.pct}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{item.level}</p>
                <p className="text-[11px] text-slate-500">{item.desc}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-white">{item.count}</p>
                <p className={`text-[11px] font-semibold ${item.color}`}>
                  ${item.earned}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "tree" && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <FaSitemap className="text-cyan-400 text-xs" />
            Referral Tree (3 Levels)
          </h3>
          {tree && tree.length > 0 ?
            <div className="overflow-x-auto scrollbar-hide -ml-2">
              {tree.map((node) => renderTreeNode(node))}
            </div>
          : <div className="text-center py-10">
              <div className="w-14 h-14 rounded-full bg-white/[0.05] flex items-center justify-center mx-auto mb-3 border border-white/[0.08]">
                <FaUsers className="text-xl text-slate-500" />
              </div>
              <p className="text-sm font-medium text-slate-400">
                No referrals yet
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Share your link to build your network
              </p>
            </div>
          }
        </div>
      )}

      {/* How it works */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
          <FaInfoCircle className="text-cyan-400 text-xs" />
          How Referral Bonuses Work
        </h3>
        <div className="space-y-2">
          {[
            "Level 1: Earn 10% ($5) when someone you invite activates",
            "Level 2: Earn 3% ($1.50) from your referrals' invites",
            "Level 3: Earn 2% ($1) from third generation referrals",
            "Bonuses are paid instantly upon activation",
            "All bonuses go directly to your wallet",
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FaCheck className="text-[8px] text-emerald-400" />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Referrals;
